import { useEffect, useRef } from 'react';

const CONTACT_INFO = [
  {
    label: 'Address',
    value: '34 blvd. CHERNI VRUH',
    icon: 'ri-map-pin-2-line',
  },
  {
    label: 'City',
    value: 'Sofia',
    icon: 'ri-building-2-line',
  },
  {
    label: 'Zip Code',
    value: '1407',
    icon: 'ri-map-2-line',
  },
  {
    label: 'Country',
    value: 'Bulgaria',
    icon: 'ri-earth-line',
  },
  {
    label: 'Email',
    value: 'help@valdestaood.com',
    icon: 'ri-mail-line',
  },
  {
    label: 'Phone Number',
    value: '+1 5074195857',
    icon: 'ri-phone-line',
  },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'translateY(40px)';
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && contentRef.current) {
            contentRef.current.style.transition = 'opacity 0.9s ease, transform 0.9s ease';
            contentRef.current.style.opacity = '1';
            contentRef.current.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="bg-brand-dark py-32">
      <div className="max-w-7xl mx-auto px-8">
        <div ref={contentRef}>
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-brand-muted" />
            <span className="text-brand-muted text-xs font-medium tracking-[0.25em] uppercase">Get in Touch</span>
          </div>

          <div className="flex items-end justify-between mb-16">
            <h2 className="font-serif text-white" style={{ fontSize: 'clamp(32px, 3.5vw, 52px)', lineHeight: '1.15', fontWeight: 700 }}>
              Contact<br />
              <em className="not-italic text-brand-muted">VALDESTA OOD</em>
            </h2>
            <p className="text-white/50 text-base max-w-sm text-right leading-relaxed">
              We are based in Sofia, Bulgaria, serving iOS users worldwide.
              Reach out to us for any inquiries.
            </p>
          </div>

          {/* Contact card */}
          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="grid grid-cols-2">
              {/* Left: Map visual / decorative */}
              <div
                className="relative overflow-hidden"
                style={{ minHeight: '380px', background: 'rgba(255,255,255,0.03)' }}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/dbab142b31f615c36604a0b447dd12a5.jpg"
                  alt="Sofia, Bulgaria — VALDESTA OOD headquarters"
                  className="w-full h-full object-cover object-top opacity-50"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/10 to-brand-dark/60" />

                {/* Location badge */}
                <div className="absolute bottom-8 left-8">
                  <div className="flex items-center gap-3 rounded-xl px-5 py-3" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <div className="w-6 h-6 flex items-center justify-center text-brand-muted">
                      <i className="ri-map-pin-fill text-sm" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Sofia, Bulgaria</p>
                      <p className="text-white/50 text-xs">34 blvd. CHERNI VRUH, 1407</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Contact details */}
              <div className="px-12 py-12 flex flex-col justify-center">
                <h3 className="font-sans font-semibold text-white text-sm tracking-widest uppercase mb-8 opacity-60">
                  Contact Information
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-7">
                  {CONTACT_INFO.map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(184,217,200,0.12)' }}>
                        <i className={`${item.icon} text-brand-muted text-sm`} />
                      </div>
                      <div>
                        <p className="text-white/40 text-xs font-medium tracking-wide uppercase mb-1">{item.label}</p>
                        <p className="text-white text-sm font-medium leading-snug">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom footer bar */}
          <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between">
            <span className="text-white/30 text-sm font-sans tracking-widest" style={{ fontWeight: 700, letterSpacing: '0.12em' }}>
              VALDESTA OOD
            </span>
            <p className="text-white/25 text-xs">
              &copy; {new Date().getFullYear()} VALDESTA OOD. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
